import { InfoCircleOutlined } from "@ant-design/icons";

/**
 * Normalized import_log stats for the summary panel.
 * Design Scope status_comment is camelCase (`created`, `corrected`, …);
 * Materials uses PascalCase (`Imported`, …). Both are accepted via
 * `normalizeImportLogStats`.
 */
export type ScopeItemsImportLogStats = {
  Total?: number;
  Imported?: number;
  Updated?: number;
  Corrected?: number;
  PartiallyAdded?: number;
  AddedUpdatedWithErrors?: number;
  Unchanged?: number;
  Failed?: number;
  File?: string;
};

type ImportLogSummaryPanelProps = {
  stats: ScopeItemsImportLogStats;
  title?: string;
  description?: string;
  onBack?: () => void;
  backLabel?: string;
};

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <>
      <div>{label}</div>
      <div className="text-right font-semibold">{value}</div>
    </>
  );
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
  }
  return undefined;
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

/** Map Design Scope camelCase (or Materials PascalCase) status_comment onto panel fields. */
export function normalizeImportLogStats(
  raw: Record<string, unknown>
): ScopeItemsImportLogStats {
  return {
    Total: firstNumber(raw.Total, raw.total),
    Imported: firstNumber(raw.Imported, raw.created),
    Updated: firstNumber(raw.Updated, raw.updated),
    Corrected: firstNumber(raw.Corrected, raw.corrected),
    PartiallyAdded: firstNumber(raw.PartiallyAdded, raw.partiallyAdded),
    AddedUpdatedWithErrors: firstNumber(raw.AddedUpdatedWithErrors),
    Unchanged: firstNumber(raw.Unchanged),
    Failed: firstNumber(raw.Failed, raw.failed),
    File: firstNonEmptyString(raw.File, raw.errorReportUrl)
  };
}

function ImportLogSummaryPanel({
  stats,
  title = "Import Summary",
  description = "Import complete. Review the summary below.",
  onBack,
  backLabel = "Back"
}: ImportLogSummaryPanelProps) {
  return (
    <div data-testid="scope-items-csv-import-summary">
      {onBack && (
        <button
          type="button"
          className="mb-4 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-ciq-primary-800"
          onClick={onBack}
          data-testid="scope-items-csv-import-summary-back"
        >
          <span className="font-semibold">{backLabel}</span>
        </button>
      )}
      <p className="mb-1 text-base font-semibold">{title}</p>
      <p className="mb-3 text-xs text-black text-opacity-50">{description}</p>
      <div
        className="grid w-full grid-cols-[1fr_auto] gap-x-4 gap-y-2 whitespace-nowrap rounded p-4"
        style={{ backgroundColor: "var(--ciq-neutral-25)" }}
      >
        <SummaryRow label="Total" value={stats.Total ?? 0} />
        <SummaryRow label="Added" value={stats.Imported ?? 0} />
        <SummaryRow label="Updated" value={stats.Updated ?? 0} />
        {stats.PartiallyAdded !== undefined && (
          <SummaryRow label="Added with Errors" value={stats.PartiallyAdded} />
        )}
        {(stats.AddedUpdatedWithErrors !== undefined ||
          stats.Unchanged !== undefined) && (
          <>
            <SummaryRow
              label="Added/Updated With Errors"
              value={stats.AddedUpdatedWithErrors ?? 0}
            />
            <SummaryRow label="Unchanged" value={stats.Unchanged ?? 0} />
          </>
        )}
        <SummaryRow label="Failed" value={stats.Failed ?? 0} />
      </div>
      <p className="mt-4 flex items-start gap-2 text-xs text-black text-opacity-60">
        <InfoCircleOutlined className="mt-0.5 shrink-0" />
        <span>
          In case of Failure/Error a failure report will automatically be
          downloaded when available.
        </span>
      </p>
    </div>
  );
}

export default ImportLogSummaryPanel;
